import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { invoiceFoldersAPI } from '@shared/apiClient';
import { getErrorMessage } from '@shared/utils';
import { FOLDER_SCOPES, SMART_FILTER_FIELDS } from './invoicingConstants';

export default function CreateFolderModal({ mailbox, isOpen, onClose, folder = null }) {
  const queryClient = useQueryClient();
  const isEditing = !!folder;
  const [name, setName] = useState('');
  const [kind, setKind] = useState('manual');
  const [rules, setRules] = useState({});
  const [scope, setScope] = useState('active');

  useEffect(() => {
    if (isOpen) {
      setName(folder?.name || '');
      setKind(folder?.kind || 'manual');
      setRules(folder?.filterRules || {});
      setScope(folder?.scope || 'active');
    }
  }, [isOpen, folder]);

  if (!isOpen) return null;

  const saveMutation = useMutation(
    () => (isEditing
      ? invoiceFoldersAPI.updateFolder(folder.id, {
        name,
        scope,
        filterRules: kind === 'smart' ? rules : undefined
      })
      : invoiceFoldersAPI.createFolder({
        mailbox,
        name,
        kind,
        scope,
        filterRules: kind === 'smart' ? rules : undefined
      })),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['invoiceFolders', mailbox]);
        toast.success(isEditing ? 'Folder updated' : 'Folder created');
        handleClose();
      },
      onError: (error) => toast.error(getErrorMessage(error))
    }
  );

  const handleClose = () => {
    setName('');
    setKind('manual');
    setRules({});
    setScope('active');
    onClose();
  };

  const toggleBooleanRule = (key) => {
    setRules((prev) => {
      const next = { ...prev };
      // Cycle: unset → true (Yes) → false (No) → unset
      if (next[key] === true) next[key] = false;
      else if (next[key] === false) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  const triStateLabel = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Any';
  };

  const setDateRule = (key, value) => {
    setRules((prev) => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  };

  const hasAtLeastOneRule = Object.keys(rules).length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Folder name is required');
      return;
    }
    if (kind === 'smart' && !hasAtLeastOneRule) {
      toast.error('Choose at least one filter rule for a smart folder');
      return;
    }
    saveMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">{isEditing ? 'Edit folder' : 'New folder'}</h3>
            <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-4 py-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Folder name</label>
              <input
                type="text"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Repairs"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Folder type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={isEditing}
                  onClick={() => setKind('manual')}
                  className={`flex-1 px-3 py-1.5 text-sm rounded border disabled:opacity-60 disabled:cursor-not-allowed ${kind === 'manual' ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                >
                  Manual
                </button>
                <button
                  type="button"
                  disabled={isEditing}
                  onClick={() => setKind('smart')}
                  className={`flex-1 px-3 py-1.5 text-sm rounded border disabled:opacity-60 disabled:cursor-not-allowed ${kind === 'smart' ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                >
                  Smart (filter-based)
                </button>
              </div>
              <p className="mt-1 text-2xs text-gray-500">
                {kind === 'manual'
                  ? 'You move items into a manual folder yourself.'
                  : 'Smart folders automatically show items matching the rules below.'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">When does an item leave this folder?</label>
              <div className="space-y-1.5">
                {FOLDER_SCOPES.map((option) => (
                  <label
                    key={option.id}
                    className={`flex items-start gap-2 rounded border p-2 cursor-pointer ${scope === option.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <input
                      type="radio"
                      name="folder-scope"
                      className="mt-0.5"
                      checked={scope === option.id}
                      onChange={() => setScope(option.id)}
                    />
                    <span>
                      <span className={`block text-sm ${scope === option.id ? 'font-medium text-primary-800' : 'text-gray-800'}`}>
                        {option.label}
                      </span>
                      <span className="block text-2xs text-gray-500">{option.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {kind === 'smart' && (
              <div className="space-y-2 border border-gray-200 rounded-md p-3 bg-gray-50">
                {SMART_FILTER_FIELDS.map((field) => (
                  <div key={field.key} className="flex items-center justify-between gap-2">
                    <label className="text-sm text-gray-700">{field.label}</label>
                    {field.type === 'tri' ? (
                      <button
                        type="button"
                        onClick={() => toggleBooleanRule(field.key)}
                        className={`min-w-[3.25rem] px-2 py-1 text-xs rounded border ${
                          rules[field.key] === true
                            ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                            : rules[field.key] === false
                              ? 'border-amber-400 bg-amber-50 text-amber-800'
                              : 'border-gray-300 bg-white text-gray-600'
                        }`}
                        title="Click to cycle: Any → Yes → No"
                      >
                        {triStateLabel(rules[field.key])}
                      </button>
                    ) : (
                      <input
                        type="date"
                        value={rules[field.key] || ''}
                        onChange={(e) => setDateRule(field.key, e.target.value)}
                        className="input text-sm w-40"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
            <button type="button" onClick={handleClose} className="btn-secondary btn-sm">Cancel</button>
            <button type="submit" disabled={saveMutation.isLoading} className="btn-primary btn-sm">
              {saveMutation.isLoading ? 'Saving...' : (isEditing ? 'Save changes' : 'Create folder')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
